import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import {SuperadminBar} from '@/components/adminBar/superadminBar';
import {Card, CardContent} from '@/components/ui/card';
import {Button} from '@/components/ui/button';
import {Navbar} from '@/components/ui/navbar';
import {toastManager} from '@/components/ui/toast';
import {getOrganizerById, checkOrganizerAdminStatus} from '@/server/organizer';
import {MembersTeaser} from '@/components/membersTeaser/membersTeaser';
import {OrganizerTabs} from '@/components/organizerTabs/organizerTabs';
import {getOrganizerMemberSummary} from '@/server/organizerMember';
import {
  ChevronLeft,
  Globe,
  Mail,
  BadgeCheck,
  LayoutDashboard,
  Plus,
} from 'lucide-react';
import {z} from 'zod';
import {FC, useEffect, useState} from 'react';
import {OrganizerDetail} from '@/apiTypes/event';

export const Route = createFileRoute('/organizer/$organizerId')({
  validateSearch: z.object({
    unauthorized: z.boolean().optional().catch(undefined),
  }),
  loader: async ({params, context}) => {
    const [organizer, membership, memberSummary] = await Promise.all([
      getOrganizerById({data: params.organizerId}),
      checkOrganizerAdminStatus({data: params.organizerId}),
      getOrganizerMemberSummary({data: {organizerId: params.organizerId}}),
    ]);
    return {
      organizer,
      isAdmin: membership.isAdmin,
      isSuperAdmin: context.profile?.level === 'superadmin',
      memberSummary,
    };
  },
  errorComponent: ({error}) => {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="px-6 text-center">
            <h1 className="text-xl font-semibold text-gray-900 mb-2 font-sans">
              Organizer Not Found
            </h1>
            <p className="text-gray-600 mb-4">
              {error?.message ||
                "The organizer you're looking for doesn't exist."}
            </p>
            <Link to="/search">
              <Button>Browse Events</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  },
  component: OrganizerLayout,
});

// ============== Sub-components ==============

/** Admin bar shown at the top of the page for organizer admins */
function OrganizerAdminBar({organizerId}: {organizerId: string}) {
  return (
    <div className="bg-slate-900 text-white">
      <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center justify-between">
        <span className="text-sm font-medium">
          You manage this organization
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="border-slate-600 text-white hover:bg-slate-800 h-8 text-xs"
            asChild
          >
            <Link
              to="/admin/dashboard/organizer/$organizerId"
              params={{organizerId}}
            >
              <LayoutDashboard className="w-3.5 h-3.5 mr-1.5" />
              Dashboard
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

interface OrganizerHeroProps {
  organizer: OrganizerDetail;
  isAdmin: boolean;
}

/** Hero section with orange gradient, organizer info, and action buttons */
const OrganizerHero: FC<OrganizerHeroProps> = ({organizer, isAdmin}) => {
  const router = useRouter();
  const [canGoBack, setCanGoBack] = useState(false);

  useEffect(() => {
    setCanGoBack(window.history.length > 1);
  }, []);

  return (
    <div>
      {/* Hero gradient */}
      <div
        className="py-10 px-4"
        style={{
          background:
            'linear-gradient(135deg, #ea580c 0%, #f97316 40%, #fb923c 70%, #f87171 100%)',
        }}
      >
        <div className="max-w-5xl mx-auto">
          {canGoBack ? (
            <Button
              variant="overlay"
              onClick={() => router.history.back()}
              className="mb-6 bg-black/30 backdrop-blur-sm text-white hover:bg-black/50 hover:text-white"
            >
              <ChevronLeft className="w-5 h-5" />
              BACK
            </Button>
          ) : null}
          <div className="flex flex-col md:flex-row gap-6 items-start">
            {/* Logo */}
            {organizer.logoUrl && (
              <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 shadow-lg">
                <img
                  src={organizer.logoUrl}
                  alt={`${organizer.name} logo`}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="flex-1">
              {/* Name + Verified badge */}
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl md:text-4xl font-bold text-white">
                  {organizer.name}
                </h1>
                {organizer.isVerified && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/20 text-white text-xs font-medium rounded-full">
                    <BadgeCheck className="w-3.5 h-3.5" />
                    Verified
                  </span>
                )}
              </div>

              {/* Meta info row */}
              <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm mb-4" />

              {/* Description */}
              {organizer.description && (
                <p className="text-white/90 text-sm md:text-base leading-relaxed mb-5 max-w-2xl">
                  {organizer.description}
                </p>
              )}

              {/* Action buttons */}
              <div className="flex flex-wrap gap-3">
                {isAdmin && (
                  <Link to="/create-event">
                    <Button className="bg-white text-orange-600 hover:bg-white/90">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Event
                    </Button>
                  </Link>
                )}
                {organizer.website && (
                  <a
                    href={organizer.website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      variant="outline"
                      className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      Visit Website
                    </Button>
                  </a>
                )}
                {organizer.contactEmail && (
                  <a href={`mailto:${organizer.contactEmail}`}>
                    <Button
                      variant="outline"
                      className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Contact
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface OrganizerStatsBarProps {
  organizer: OrganizerDetail;
}

/** Horizontal stats bar showing key metrics */
const OrganizerStatsBar: FC<OrganizerStatsBarProps> = ({organizer}) => {
  return (
    <div className="border-b border-gray-200 bg-white">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div>
          <p className="text-2xl font-bold text-orange-600">
            {organizer.totalEvents}
          </p>
          <p className="text-sm text-gray-600">Total Events</p>
        </div>
      </div>
    </div>
  );
};

// ============== Main Layout Component ==============

/** Parent layout for the organizer page — owns hero, stats bar, admin bars, and the child outlet */
function OrganizerLayout() {
  const {organizer, isAdmin, isSuperAdmin, memberSummary} =
    Route.useLoaderData();
  const {unauthorized} = Route.useSearch();
  const navigate = useNavigate();

  useEffect(() => {
    if (unauthorized) {
      toastManager.add({
        title: 'You do not have permissions to edit this organizer',
        type: 'error',
      });
      navigate({
        to: '/organizer/$organizerId',
        params: {organizerId: organizer.id},
        search: {unauthorized: undefined},
        replace: true,
      });
    }
  }, [unauthorized, organizer.id, navigate]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      {/* Admin bar — regular member bar takes priority over superadmin bar */}
      {isAdmin && <OrganizerAdminBar organizerId={organizer.id} />}
      {isSuperAdmin && !isAdmin && (
        <SuperadminBar
          to="/admin/organizer/$organizerId"
          params={{organizerId: organizer.id}}
        />
      )}
      <OrganizerHero organizer={organizer} isAdmin={isAdmin} />
      <OrganizerStatsBar organizer={organizer} />
      <div className="max-w-5xl mx-auto w-full px-4 py-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
          <main className="flex flex-col flex-1 gap-4 lg:order-1 min-w-0 lg:pr-8 lg:border-r lg:border-neutral-200">
            {memberSummary.totalCount > 0 && (
              <OrganizerTabs organizerId={organizer.id} />
            )}
            <Outlet />
          </main>
          {memberSummary.totalCount > 0 && (
            <aside className="hidden lg:block lg:order-2 lg:w-[280px] lg:shrink-0">
              <MembersTeaser
                organizerId={organizer.id}
                summary={memberSummary}
              />
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}
